import { useState, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { BellIcon, BellSlashIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { updateNotificationPreferences } from '../store/slices/authSlice';
import { BODACC_FAMILY_TYPES } from '../utils/bodaccUtils';
import { Listbox, Transition } from '@headlessui/react';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';

type NotificationType = 'anomaly' | 'prospection';

interface NotificationPreferencesProps {
  type: NotificationType;
}

interface FrequencyOption {
  id: 'weekly' | 'biweekly' | 'monthly';
  name: string;
  disabled?: boolean;
}

const getFrequencies = (type: NotificationType): FrequencyOption[] => {
  const baseFrequencies: FrequencyOption[] = [
    { id: 'weekly', name: 'Hebdomadaire' },
    { id: 'biweekly', name: 'Bimensuelle' },
    { id: 'monthly', name: 'Mensuelle' }
  ];
  
  // For prospection, only make monthly available and mark others as coming soon
  if (type === 'prospection') {
    return [
      { id: 'weekly', name: 'Hebdomadaire (Bientôt disponible)', disabled: true },
      { id: 'biweekly', name: 'Bimensuelle (Bientôt disponible)', disabled: true },
      { id: 'monthly', name: 'Mensuelle', disabled: false }
    ];
  }
  
  return baseFrequencies;
};

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ type }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);
  const error = useSelector((state: RootState) => state.auth.error);
  
  // State for notification preferences
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('monthly');
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  
  // Get frequencies based on notification type
  const frequencies = getFrequencies(type);
  
  // Initialize state based on user data
  useEffect(() => {
    if (currentUser) {
      if (type === 'anomaly') {
        setEnabled(currentUser.anomalyNotificationsEnabled || false);
        setFrequency(currentUser.anomalyNotificationFrequency || 'monthly');
        setSelectedFamilies(currentUser.anomalyBodaccFamilies || []);
      } else if (type === 'prospection') {
        setEnabled(currentUser.prospectionNotificationsEnabled || false);
        // Force monthly for prospection
        setFrequency('monthly');
      }
    }
  }, [currentUser, type]);

  // Show error toast when there's an error from Redux
  useEffect(() => {
    if (error) {
      showErrorToast(error, `notification-error-${type}`);
    }
  }, [error, type]);
  
  const handleToggle = () => {
    setEnabled(!enabled);
  };
  
  const handleFrequencyChange = (newFrequency: 'weekly' | 'biweekly' | 'monthly') => {
    // For prospection, only allow monthly
    if (type === 'prospection' && newFrequency !== 'monthly') {
      return;
    }
    setFrequency(newFrequency);
  };
  
  const handleSave = async () => {
    const payload: Record<string, any> = {};
    
    if (type === 'anomaly') {
      payload.anomalyNotificationsEnabled = enabled;
      payload.anomalyNotificationFrequency = frequency;
      payload.anomalyBodaccFamilies = selectedFamilies.length > 0 ? selectedFamilies : null;
    } else if (type === 'prospection') {
      payload.prospectionNotificationsEnabled = enabled;
      payload.prospectionNotificationFrequency = 'monthly'; // Always use monthly for prospection
    }
    
    try {
      await dispatch(updateNotificationPreferences(payload) as any);
      showSuccessToast('Préférences de notification mises à jour avec succès', `notification-updated-${type}`);
    } catch (err) {
      console.error('Error updating notification preferences:', err);
    }
  };
  
  const getTitle = () => {
    return type === 'anomaly' 
      ? 'Notifications des anomalies' 
      : 'Notifications de prospection';
  };
  
  const getDescription = () => {
    return type === 'anomaly'
      ? 'Recevez des notifications sur les anomalies détectées concernant vos adhérents.'
      : 'Recevez des notifications sur les opportunités de prospection de nouveaux adhérents.';
  };

  // Get the current selected frequency object
  const selectedFrequency = frequencies.find(f => f.id === frequency) as FrequencyOption || frequencies[2];
  
  return (
    <div className="bg-white shadow rounded-lg p-4 mb-4">
      <h3 className="text-lg font-medium text-gray-900">{getTitle()}</h3>
      <p className="text-sm text-gray-500 mt-1 mb-4">{getDescription()}</p>
      
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {enabled ? (
              <BellIcon className="h-5 w-5 text-indigo-600 mr-2" />
            ) : (
              <BellSlashIcon className="h-5 w-5 text-gray-400 mr-2" />
            )}
            <span className="text-sm font-medium text-gray-700">
              {enabled ? 'Notifications activées' : 'Notifications désactivées'}
            </span>
          </div>
          
          <button
            type="button"
            onClick={handleToggle}
            className={`${
              enabled ? 'bg-indigo-600' : 'bg-gray-200'
            } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2`}
          >
            <span className="sr-only">Activer les notifications</span>
            <span
              aria-hidden="true"
              className={`${
                enabled ? 'translate-x-5' : 'translate-x-0'
              } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
          </button>
        </div>
        
        {enabled && (
          <div>
            <label htmlFor={`${type}-frequency`} className="block text-sm font-medium text-gray-700 mb-1">
              Fréquence des notifications
            </label>
            <Listbox value={frequency} onChange={handleFrequencyChange}>
              <div className="relative mt-1">
                <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left border border-gray-300 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                  <span className="block truncate">{selectedFrequency.name}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {frequencies.map((freq) => (
                      <Listbox.Option
                        key={freq.id}
                        value={freq.id}
                        disabled={freq.disabled ?? false}
                        className={({ active, disabled }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active && !disabled ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                          } ${
                            disabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {freq.name}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
            {type === 'prospection' && (
              <p className="mt-1 text-xs text-gray-500">
                Seule la fréquence mensuelle est disponible pour le moment.
              </p>
            )}
          </div>
        )}
        {enabled && type === 'anomaly' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Être notifié pour les types d'enregistrements suivants
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(BODACC_FAMILY_TYPES).map(([key, label]) => {
                const checked = selectedFamilies.includes(key);
                return (
                  <label key={key} className="inline-flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={checked}
                      onChange={(e) => {
                        setSelectedFamilies((prev) => {
                          if (e.target.checked) {
                            return Array.from(new Set([...prev, key]));
                          }
                          return prev.filter((k) => k !== key);
                        });
                      }}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Si aucune case n'est cochée, tous les types seront inclus par défaut.
            </p>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <div className="mt-1">
            <input
              type="text"
              readOnly
              value={currentUser?.email || ''}
              className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              Les notifications seront envoyées à cette adresse email.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences; 